import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";
import { websocketRetryInterval, websocketUrl } from "../../settings/shared/constants";

const WebSocketContext = createContext(null);

export const WebSocketProvider = ({ children }) => {
  const socketRef = useRef(null);
  const [messages, setMessages] = useState([]);
  const [isConnected, setIsConnected] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0); // Key to trigger component refresh
  const reconnectTimeoutRef = useRef(null);
  const connectionCheckIntervalRef = useRef(null);
  const isReconnectingRef = useRef(false);
  const reconnectAttemptsRef = useRef(0);
  const alertShownRef = useRef(false);
  const currentSocketAttemptRef = useRef(null); // Track current socket to avoid double counting

  const connect = useCallback(() => {
    const token = localStorage.getItem("accessToken");
    if (!token) {
      setIsConnected(false);
      return;
    }
    
    // Close existing socket if any
    if (socketRef.current) {
      socketRef.current.close();
    }
    
    const socket = new WebSocket(`${websocketUrl}?token=${token}`);
    socketRef.current = socket;
    currentSocketAttemptRef.current = socket; // Track this socket attempt

    socket.onopen = () => {
      setIsConnected(true);
      isReconnectingRef.current = false;
      reconnectAttemptsRef.current = 0; // Reset attempts on successful connection
      alertShownRef.current = false; // Reset alert flag on successful connection
      currentSocketAttemptRef.current = null; // Clear tracking
    };

    socket.onmessage = (event) => {
      setMessages((prev) => [...prev, event.data]);
      window.dispatchEvent(new CustomEvent("messageReceived", { detail: event.data }));
    };

    socket.onclose = () => {
      setIsConnected(false);
      
      // Only count this attempt if it's the current socket and hasn't been counted yet
      if (currentSocketAttemptRef.current === socket) {
        reconnectAttemptsRef.current++;
        currentSocketAttemptRef.current = null; // Mark as counted
        
        // Refresh component if more than 5 attempts
        if (reconnectAttemptsRef.current > 5 && !alertShownRef.current) {
          alertShownRef.current = true;
          // Clear any pending reconnection
          if (reconnectTimeoutRef.current) {
            clearTimeout(reconnectTimeoutRef.current);
            reconnectTimeoutRef.current = null;
          }
          // Reset connection state and trigger refresh
          setIsConnected(false);
          setRefreshKey(prev => prev + 1); // Trigger component refresh
          // Force a fresh connection attempt after a delay
          setTimeout(() => {
            reconnectAttemptsRef.current = 0;
            alertShownRef.current = false;
            isReconnectingRef.current = false;
            connect();
          }, 1000);
          return; // Don't call reconnect() when refreshing
        }
      }
      
      reconnect();
    };

    socket.onerror = (error) => {
      setIsConnected(false);
      socket.close();
    };
  }, []);

  const reconnect = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
    }
    if (!isReconnectingRef.current) {
      isReconnectingRef.current = true;
      reconnectTimeoutRef.current = setTimeout(() => {
        isReconnectingRef.current = false;
        connect();
      }, websocketRetryInterval);
    }
  }, [connect]);

  /**
   * Force reconnect by calling connect directly
   * This can be used to refresh the connection without waiting for retry interval
   */
  const forceReconnect = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
    }
    isReconnectingRef.current = false;
    connect();
  }, [connect]);

  /**
   * Check if WebSocket is connected
   * @returns {boolean} True if connected, false otherwise
   */
  const checkConnection = useCallback(() => {
    const isActuallyConnected = socketRef.current?.readyState === WebSocket.OPEN;
    // Sync state if there's a mismatch (using functional update to avoid dependency)
    setIsConnected(prevConnected => {
      if (isActuallyConnected !== prevConnected) {
        return isActuallyConnected;
      }
      return prevConnected;
    });
    return isActuallyConnected;
  }, []);

  /**
   * Attempt to reconnect WebSocket with retry limit
   * @param {number} maxRetries - Maximum number of reconnection attempts (default: 5)
   * @param {number} delay - Delay between retries in milliseconds (default: 1000)
   * @returns {Promise<boolean>} Promise that resolves to true if connected, false if failed after all retries
   */
  const reconnectWithRetry = useCallback(async (maxRetries = 5, delay = 800) => {
    return new Promise((resolve) => {
      let attempts = 0;
      let timeoutId = null;
      let currentSocket = null;
      
      const cleanup = () => {
        if (timeoutId) {
          clearTimeout(timeoutId);
          timeoutId = null;
        }
        if (currentSocket && currentSocket !== socketRef.current) {
          currentSocket.close();
        }
      };
      
      const tryReconnect = () => {
        attempts++;
        
        // Close existing socket if any (but keep the main socketRef if it's different)
        if (currentSocket && currentSocket !== socketRef.current) {
          currentSocket.close();
        }
        
        const token = localStorage.getItem("accessToken");
        if (!token) {
          cleanup();
          resolve(false);
          return;
        }
        
        // Create a new socket for this retry attempt
        const socket = new WebSocket(`${websocketUrl}?token=${token}`);
        currentSocket = socket;
        
        // Set timeout for connection attempt
        timeoutId = setTimeout(() => {
          if (socket.readyState !== WebSocket.OPEN) {
            socket.close();
            if (attempts < maxRetries) {
              timeoutId = setTimeout(tryReconnect, delay);
            } else {
              cleanup();
              if (attempts >= 5) {
                setIsConnected(false);
                setRefreshKey(prev => prev + 1); // Trigger component refresh
                // Force a fresh connection attempt
                setTimeout(() => {
                  connect();
                }, 1000);
              }
              resolve(false);
            }
          }
        }, delay);
        
        socket.onopen = () => {
          cleanup();
          // Update the main socket reference
          socketRef.current = socket;
          setIsConnected(true);
          
          // Set up message handler
          socket.onmessage = (event) => {
            setMessages((prev) => [...prev, event.data]);
            window.dispatchEvent(new CustomEvent("messageReceived", { detail: event.data }));
          };
          
          // Set up close handler - use the existing reconnect logic
          socket.onclose = () => {
            setIsConnected(false);
            if (reconnectTimeoutRef.current) {
              clearTimeout(reconnectTimeoutRef.current);
            }
            reconnectTimeoutRef.current = setTimeout(connect, websocketRetryInterval);
          };
          
          // Set up error handler
          socket.onerror = (error) => {
            setIsConnected(false);
            socket.close();
          };
          
          resolve(true);
        };
        
        socket.onclose = () => {
          cleanup();
          setIsConnected(false);
          if (attempts < maxRetries) {
            timeoutId = setTimeout(tryReconnect, delay);
          } else {
            if (attempts >= 5) {
              setIsConnected(false);
              setRefreshKey(prev => prev + 1); // Trigger component refresh
              // Force a fresh connection attempt
              setTimeout(() => {
                connect();
              }, 1000);
            }
            resolve(false);
          }
        };
        
        socket.onerror = () => {
          cleanup();
          if (attempts < maxRetries) {
            timeoutId = setTimeout(tryReconnect, delay);
          } else {
            if (attempts >= 5) {
              setIsConnected(false);
              setRefreshKey(prev => prev + 1); // Trigger component refresh
              // Force a fresh connection attempt
              setTimeout(() => {
                connect();
              }, 1000);
            }
            resolve(false);
          }
        };
      };
      
      tryReconnect();
    });
  }, [connect]);

  useEffect(() => {
    connect();
    
    // Set up periodic connection check (every 5 seconds)
    connectionCheckIntervalRef.current = setInterval(() => {
      const isActuallyConnected = socketRef.current?.readyState === WebSocket.OPEN;
      
      // Update state if there's a mismatch
      setIsConnected(prevConnected => {
        if (isActuallyConnected !== prevConnected) {
          return isActuallyConnected;
        }
        return prevConnected;
      });
      
      // If disconnected and not already reconnecting, attempt to reconnect
      if (!isActuallyConnected && !isReconnectingRef.current && socketRef.current?.readyState === WebSocket.CLOSED) {
        console.log('WebSocket disconnected detected, attempting to reconnect...');
        reconnect();
      }
    }, 5000); // Check every 5 seconds
    
    return () => {
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      if (connectionCheckIntervalRef.current) {
        clearInterval(connectionCheckIntervalRef.current);
      }
      if (socketRef.current) {
        socketRef.current.close();
      }
    };
  }, [connect, reconnect]);

  // Function to send messages
  const sendMessage = useCallback((msg) => {
    if (socketRef.current && socketRef.current.readyState === WebSocket.OPEN) {
      socketRef.current.send(msg);
    } 
  }, []);

  return (
    <WebSocketContext.Provider value={{ 
      socket: socketRef.current, 
      messages, 
      sendMessage, 
      isConnected,
      checkConnection,
      reconnectWithRetry,
      forceReconnect,
      connect,
      refreshKey // Expose refreshKey so components can react to it
    }}>
      {children}
    </WebSocketContext.Provider>
  );
};

// Hook to use WebSocket in components
export const useWebSocket = () => useContext(WebSocketContext);
